/**
 * Utility functions for grouping campaigns by title
 */

/**
 * Extract the title from a campaign name (text after the colon)
 * Example: "[RSOC 4] RSOC Auto [knowledgia]: Costco Diamond Rings" -> "Costco Diamond Rings"
 */
export const extractCampaignTitle = (campaignName) => {
  if (!campaignName) return null;

  const colonIndex = campaignName.indexOf(':');
  if (colonIndex === -1) return null;

  return campaignName.substring(colonIndex + 1).trim();
};

/**
 * Clean campaign name by removing common prefixes and brackets
 * Removes: "[RSOC X]", "[domain]", "Widget RSOC Auto", "RSOC Auto", "WAVESHARE -"
 */
export const cleanCampaignName = (campaignName) => {
  if (!campaignName) return '';

  let cleaned = campaignName;

  // Remove WAVESHARE prefix
  cleaned = cleaned.replace(/^WAVESHARE\s*-\s*/i, '');

  // Remove all bracketed content [anything]
  cleaned = cleaned.replace(/\[[^\]]+\]\s*/g, '');

  // Remove "Widget RSOC Auto" or "RSOC Auto" prefix (with optional colon)
  cleaned = cleaned.replace(/^Widget\s+RSOC\s+Auto\s*:?\s*/i, '');
  cleaned = cleaned.replace(/^RSOC\s+Auto\s*:?\s*/i, '');

  // Remove leading colon if present
  cleaned = cleaned.replace(/^:\s*/, '');

  return cleaned.trim();
};

/**
 * Extract account badge info from campaign name
 * Example: "[RSOC 4] Widget RSOC Auto [knowledgia]: Title" -> { account: "RSOC 4", type: "Widget" }
 * Example: "[RSOC 2] RSOC Auto [domain]: Title" -> { account: "RSOC 2", type: null }
 * Example: "WAVESHARE - [RSOC 4] ..." -> { account: "RSOC 4", type: null }
 */
export const extractAccountBadge = (campaignName) => {
  if (!campaignName) return null;

  // Look for [RSOC X] pattern anywhere in the string (handles WAVESHARE prefix)
  const accountMatch = campaignName.match(/\[(RSOC\s*\d+)\]/i);
  const account = accountMatch ? accountMatch[1] : null;

  // Check if it's Widget RSOC Auto or regular RSOC Auto
  const isWidget = /Widget\s+RSOC\s+Auto/i.test(campaignName);

  if (!account) return null;

  return {
    account,
    type: isWidget ? 'Widget' : null
  };
};

/**
 * Extract the domain/page identifier from campaign name
 * Example: "[RSOC 4] RSOC Auto [knowledgia]: ..." -> "knowledgia"
 * Example: "[RSOC 4] Widget RSOC Auto [thecozyguide]: ..." -> "thecozyguide"
 */
export const extractDomainIdentifier = (campaignName) => {
  if (!campaignName) return 'unknown';

  // Match all text inside square brackets
  const matches = campaignName.match(/\[([^\]]+)\]/g);
  if (matches) {
    // Look for any bracket that contains a domain (not "RSOC X" pattern)
    for (const match of matches) {
      const content = match.slice(1, -1); // Remove brackets
      // Skip numeric identifiers like "RSOC 4", "RSOC 5", etc.
      if (!/^RSOC\s*\d+$/i.test(content) && !/^\d+$/.test(content)) {
        return content;
      }
    }
  }

  return 'unknown';
};

/**
 * Group campaigns by their title (text after colon)
 * Returns an array of groups, where each group has:
 * - title: the common title
 * - campaigns: array of individual campaigns
 * - aggregated: combined metrics for the group
 */
export const groupCampaignsByTitle = (campaigns) => {
  if (!campaigns || campaigns.length === 0) return [];

  const groups = new Map();
  const ungrouped = [];

  campaigns.forEach(campaign => {
    const title = extractCampaignTitle(campaign.campaign_name);

    if (title) {
      if (!groups.has(title)) {
        groups.set(title, []);
      }
      groups.get(title).push(campaign);
    } else {
      // Campaigns without a colon in the name go to ungrouped
      ungrouped.push(campaign);
    }
  });

  const result = [];

  // Process groups
  groups.forEach((groupCampaigns, title) => {
    if (groupCampaigns.length > 1) {
      // Multiple campaigns with same title - create a group
      result.push({
        isGroup: true,
        title: title,
        campaigns: groupCampaigns,
        aggregated: aggregateMetrics(groupCampaigns),
        // Use the first campaign's ID as a base for the group key
        groupId: `group-${groupCampaigns[0].campaign_id}`
      });
    } else {
      // Single campaign - no need for grouping
      result.push({
        isGroup: false,
        campaign: groupCampaigns[0]
      });
    }
  });

  // Add ungrouped campaigns
  ungrouped.forEach(campaign => {
    result.push({
      isGroup: false,
      campaign: campaign
    });
  });

  return result;
};

/**
 * Aggregate metrics for a group of campaigns
 */
export const aggregateMetrics = (campaigns) => {
  const aggregated = {
    clicks: 0,
    impressions: 0,
    demand_clicks: 0,
    demand_visits: 0,
    cost: 0,
    revenue: 0,
    adsense_revenue: 0,
    compado_revenue: 0
  };

  campaigns.forEach(campaign => {
    aggregated.clicks += parseInt(campaign.clicks || 0, 10);
    aggregated.impressions += parseInt(campaign.impressions || 0, 10);
    aggregated.demand_clicks += parseInt(campaign.demand_clicks || 0, 10);
    aggregated.demand_visits += parseInt(campaign.demand_visits || 0, 10);
    aggregated.cost += parseFloat(campaign.cost || 0);
    aggregated.revenue += parseFloat(campaign.revenue || 0);
    aggregated.adsense_revenue += parseFloat(campaign.adsense_revenue || 0);
    aggregated.compado_revenue += parseFloat(campaign.compado_revenue || 0);
  });

  // Calculate derived metrics
  aggregated.profit = aggregated.revenue - aggregated.cost;
  aggregated.roas = aggregated.cost > 0 ? aggregated.revenue / aggregated.cost : 0;
  aggregated.ctr = aggregated.impressions > 0 ? (aggregated.clicks / aggregated.impressions) * 100 : 0;
  // EPC: revenue / conversions (demand_clicks)
  aggregated.payout = aggregated.demand_clicks > 0 ? aggregated.revenue / aggregated.demand_clicks : 0;
  aggregated.cpc = aggregated.clicks > 0 ? aggregated.cost / aggregated.clicks : 0;

  // CVR: demand_clicks / clicks
  aggregated.cvr = aggregated.clicks > 0 ? (aggregated.demand_clicks / aggregated.clicks) * 100 : 0;

  return aggregated;
};

/**
 * Sort groups by a specific metric (uses aggregated values for groups)
 */
export const sortGroupedCampaigns = (groupedData, sortField, sortDirection) => {
  const getValue = (item) => {
    if (item.isGroup) {
      // Use aggregated value for groups
      switch (sortField) {
        case 'clicks':
          return item.aggregated.clicks;
        case 'impressions':
          return item.aggregated.impressions;
        case 'cost':
          return item.aggregated.cost;
        case 'revenue':
          return item.aggregated.revenue;
        case 'profit':
          return item.aggregated.profit;
        case 'roas':
          return item.aggregated.roas;
        case 'ctr':
          return item.aggregated.ctr;
        case 'payout':
          return item.aggregated.payout;
        case 'cpc':
          return item.aggregated.cpc;
        case 'campaign_id':
        case 'campaign_name':
          return item.title || '';
        default:
          return item.aggregated[sortField] || 0;
      }
    } else {
      // Use campaign value for ungrouped
      const campaign = item.campaign;
      switch (sortField) {
        case 'campaign_id':
        case 'campaign_name':
          return campaign[sortField] || '';
        default:
          return parseFloat(campaign[sortField] || 0);
      }
    }
  };

  return [...groupedData].sort((a, b) => {
    const valA = getValue(a);
    const valB = getValue(b);

    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }

    return sortDirection === 'asc' ? valA - valB : valB - valA;
  });
};
